import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { vendorAuthService } from '../services/vendorAuthService';

interface UseResetTokenValidationReturn {
  isLoading: boolean;
  validationError: string | null;
  hasToken: boolean;
  hasValidated: boolean;
  isValidToken: boolean;
  vendorEmail: string | null;
}

export const useResetTokenValidation = (): UseResetTokenValidationReturn => {
  // Hook for validating password reset tokens and getting vendor email
  const [searchParams] = useSearchParams();
  const [isValidating, setIsValidating] = useState(true);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [hasValidated, setHasValidated] = useState(false);
  const [isValidToken, setIsValidToken] = useState(false);
  const [vendorEmail, setVendorEmail] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get('token');
    
    const validateResetToken = async () => {
      if (!token) {
        setValidationError('No reset token provided. Please use the link from your password reset email.');
        setIsValidToken(false);
        setIsValidating(false);
        setHasValidated(true);
        return;
      }

      try {
        const response = await vendorAuthService.validateResetToken(token);
        if (response.success && response.vendor) {
          setIsValidToken(true);
          setVendorEmail(response.vendor.email);
          // Clear the token from the URL after successful validation
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('token');
          const newUrl = `${window.location.pathname}${newSearchParams.toString() ? `?${newSearchParams.toString()}` : ''}`;
          window.history.replaceState({}, '', newUrl);
        } else {
          setValidationError('Invalid or expired reset token. Please request a new one.');
          setIsValidToken(false);
        }
      } catch (error: any) {
        console.error('Reset token validation error:', error);
        setValidationError(error.response?.data?.error || 'Failed to validate reset token. Please try again.');
        setIsValidToken(false);
      } finally {
        setIsValidating(false);
        setHasValidated(true);
      }
    };

    if (!hasValidated) {
      validateResetToken();
    }
  }, [searchParams, hasValidated]);

  return {
    isLoading: isValidating,
    validationError,
    hasToken: !!searchParams.get('token'),
    hasValidated,
    isValidToken,
    vendorEmail
  };
};
